// src/dnd/emitter_tool/ui/GlowPanel.jsx (full file)
import React, { useEffect, useMemo, useState } from "react";

const OPEN_KEY = "EmitterTool:GlowPanelOpenV1";

function CollapsibleBox({ title, defaultOpen = true, children }) {
  const [open, setOpen] = useState(() => {
    try {
      const saved = localStorage.getItem(OPEN_KEY);
      return saved == null ? defaultOpen : saved === "1";
    } catch {
      return defaultOpen;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(OPEN_KEY, open ? "1" : "0");
    } catch {}
  }, [open]);

  return (
    <div
      style={{
        border: "1px solid #334155",
        borderRadius: 8,
        marginBottom: 12,
        background: "#0b0f17",
        maxWidth: "100%",
        boxSizing: "border-box",
        overflowX: "hidden",
      }}
    >
      <div
        onClick={() => setOpen((v) => !v)}
        style={{
          padding: "8px 10px",
          background: "#18212f",
          userSelect: "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontWeight: 600,
          gap: 8,
        }}
      >
        <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {title}
        </span>
        <span style={{ opacity: 0.75, flex: "0 0 auto" }}>{open ? "▾" : "▸"}</span>
      </div>
      {open && <div style={{ padding: 10 }}>{children}</div>}
    </div>
  );
}

const sectionHeader = { fontSize: 12, fontWeight: 600, opacity: 0.9, margin: "6px 0" };
const labelStyle = { fontSize: 12, opacity: 0.85 };

const stackedBlock = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
  marginBottom: 10,
};

const selectStyle = {
  width: "100%",
  maxWidth: "100%",
  background: "#0b0d12",
  color: "white",
  border: "1px solid #374151",
  borderRadius: 8,
  padding: "6px 8px",
  fontSize: 12,
  appearance: "none",
  WebkitAppearance: "none",
  MozAppearance: "none",
  cursor: "pointer",
  boxSizing: "border-box",
  display: "block",
};

function clamp(val, min, max) { return Math.max(min, Math.min(max, val)); }
function toNum(v, fallback = 0) { const n = Number(v); return Number.isFinite(n) ? n : fallback; }

function RowSlider({ label, value, onChange, min = 0, max = 10, step = 0.1, suffix = "" }) {
  const [text, setText] = useState(Number.isFinite(value) ? String(value) : String(min));
  useEffect(() => {
    const asStr = String(Number.isFinite(value) ? value : min);
    if (asStr !== text) setText(asStr);
  }, [value, min, max]); // eslint-disable-line

  const commitNumber = (raw) => {
    const parsed = toNum(raw, value ?? min);
    const clamped = clamp(parsed, min, max);
    onChange?.(clamped);
  };

  return (
    <div style={stackedBlock}>
      <div style={labelStyle}>{label}</div>

      <input
        type="range"
        value={Number.isFinite(value) ? value : min}
        min={min}
        max={max}
        step={step}
        onChange={(e) => { const v = toNum(e.target.value, min); onChange?.(v); }}
        style={{
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
          accentColor: "#64748b",
          cursor: "pointer",
        }}
      />

      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <input
          type="number"
          value={text}
          min={min}
          max={max}
          step={step}
          onChange={(e) => setText(e.target.value)}
          onBlur={(e) => commitNumber(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
          style={{
            width: "120px",
            background: "#0b0d12",
            color: "white",
            border: "1px solid #374151",
            borderRadius: 6,
            padding: "4px 6px",
            fontSize: 12,
            textAlign: "right",
            boxSizing: "border-box",
          }}
        />
        {suffix ? <span style={{ opacity: 0.7, whiteSpace: "nowrap" }}>{suffix}</span> : null}
      </div>
    </div>
  );
}

function LabeledSelect({ label, value, onChange, children }) {
  return (
    <div style={{ ...stackedBlock, marginBottom: 8 }}>
      <div style={labelStyle}>{label}</div>
      <select value={value} onChange={onChange} style={selectStyle}>
        {children}
      </select>
    </div>
  );
}

export default function GlowPanel({
  effectSettings = {},
  setEffectSettings,
  title = "Glow",
  defaultOpen = true,
  pxPerMeter = 80, // optional: used to convert when flipping units
}) {
  // --- scope routing --------------------------------------------------------
  // default to parent until SubEmittersPanel announces a child
  const [scope, setScope] = useState({ type: "parent", id: "parent" });

  useEffect(() => {
    const onScope = (e) => {
      const s = e?.detail;
      if (!s?.scopeType) return;
      if (s.scopeType === "child" && s.scopeId) setScope({ type: "child", id: String(s.scopeId) });
      else setScope({ type: "parent", id: "parent" });
    };
    try { window.addEventListener("EmitterTool:ScopeChanged", onScope); } catch {}
    return () => { try { window.removeEventListener("EmitterTool:ScopeChanged", onScope); } catch {} };
  }, []);

  const scopedEventName =
    scope.type === "child"
      ? `EmitterTool:GlowConfig:child:${scope.id}`
      : `EmitterTool:GlowConfig:parent`;

  const dispatchGlowConfig = (payload) => {
    // Emit to the scoped channel (child or parent) so only that GlowManager updates
    try { window.dispatchEvent(new CustomEvent(scopedEventName, { detail: payload })); } catch {}
    // Emit legacy/global for backward compatibility with parent listeners still on the old name
    try { window.dispatchEvent(new CustomEvent("EmitterTool:GlowConfig", { detail: payload })); } catch {}
  };

  const setES = (patch) => {
    if (typeof setEffectSettings === "function") {
      setEffectSettings((cur) => {
        const base = cur || {};
        const nextPatch = typeof patch === "function" ? patch(base) : patch;
        const merged = { ...base, ...nextPatch };
        dispatchGlowConfig(merged);
        return merged;
      });
    } else {
      // Even if no setEffectSettings provided, still broadcast so runtime reacts
      const nextPatch = typeof patch === "function" ? patch(effectSettings || {}) : patch;
      const merged = { ...(effectSettings || {}), ...nextPatch };
      dispatchGlowConfig(merged);
    }
  };

  // read values (with defaults)
  const {
    glowEnabled = false,
    glowPercent = 100,
    glowIntensity = 1,
    glowReachM = 10,
    glowReachPx = 0,
    glowDurationS = 0.75,
    tintEnabled = false,
    tintStart = "#ffffff",
    tintEnd = "#ffffff",
  } = effectSettings || {};

  // Unit state is derived from which reach field is effectively active.
  const initialUnit = useMemo(() => (glowReachPx > 0 ? "px" : "m"), [glowReachPx]);
  const [reachUnit, setReachUnit] = useState(initialUnit);

  useEffect(() => {
    // keep unit in sync if external effectSettings flips it
    const next = glowReachPx > 0 ? "px" : "m";
    if (next !== reachUnit) setReachUnit(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [glowReachPx]);

  // Change handlers for reach that keep one field authoritative:
  const setReachM = (m) => {
    const v = Math.max(0, m);
    setES({ glowReachM: v, glowReachPx: 0 }); // clear px to avoid ambiguity
  };
  const setReachPx = (px) => {
    const v = Math.max(0, px);
    setES({ glowReachPx: v, glowReachM: 0 }); // clear m to avoid ambiguity
  };

  const onChangeUnit = (e) => {
    const next = e.target.value === "px" ? "px" : "m";
    if (next === reachUnit) return;
    setReachUnit(next);

    // Convert the currently meaningful value when flipping.
    if (next === "px") {
      const px = glowReachPx > 0 ? glowReachPx : (Math.max(0, glowReachM) * (pxPerMeter || 80));
      setReachPx(px);
    } else {
      const m = glowReachM > 0 ? glowReachM : ((pxPerMeter || 80) > 0 ? Math.max(0, glowReachPx) / (pxPerMeter || 80) : 0);
      setReachM(m);
    }
  };

  // UI ranges: generous but sane
  const MAX_M  = 50;
  const MAX_PX = 2000;

  return (
    <CollapsibleBox title={title} defaultOpen={defaultOpen}>
      <div style={{ maxWidth: "100%", overflowX: "hidden" }}>

        {/* Enable */}
        <div style={sectionHeader}>Enable</div>
        <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <input
            type="checkbox"
            checked={!!glowEnabled}
            onChange={(e) => setES({ glowEnabled: e.target.checked })}
            style={{ accentColor: "#64748b" }}
          />
          <span style={{ fontSize: 12 }}>
            Enable per-particle glow {scope.type === "child" ? `(child: ${scope.id})` : "(parent)"}
          </span>
        </label>

        {/* Performance */}
        <div style={sectionHeader}>Performance</div>
        <RowSlider
          label="Glow percent (%)"
          value={glowPercent}
          onChange={(v) => setES({ glowPercent: Math.round(clamp(v, 0, 100)) })}
          min={0}
          max={100}
          step={1}
          suffix="%"
        />

        {/* Effect */}
        <div style={sectionHeader}>Effect</div>
        <RowSlider
          label="Intensity"
          value={glowIntensity}
          onChange={(v) => setES({ glowIntensity: clamp(v, 0, 1) })}
          min={0}
          max={1}
          step={0.01}
        />

        {/* Size / Reach */}
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 120px", gap: 8, alignItems: "end" }}>
          <div>
            <RowSlider
              label={`Reach (${reachUnit === "px" ? "pixels" : "metres"})`}
              value={reachUnit === "px" ? Math.max(0, glowReachPx) : Math.max(0, glowReachM)}
              onChange={(v) => (reachUnit === "px" ? setReachPx(v) : setReachM(v))}
              min={0}
              max={reachUnit === "px" ? MAX_PX : MAX_M}
              step={reachUnit === "px" ? 1 : 0.1}
              suffix={reachUnit === "px" ? "px" : "m"}
            />
          </div>
          <div>
            <LabeledSelect
              label="Units"
              value={reachUnit}
              onChange={onChangeUnit}
            >
              <option value="m">metres</option>
              <option value="px">pixels</option>
            </LabeledSelect>
          </div>
        </div>

        <RowSlider
          label="Duration (seconds)"
          value={glowDurationS}
          onChange={(v) => setES({ glowDurationS: Math.max(0, v) })}
          min={0}
          max={5}
          step={0.05}
          suffix="s"
        />

        {/* Optional tint */}
        <div style={sectionHeader}>Optional tint (colorize)</div>
        <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <input
            type="checkbox"
            checked={!!tintEnabled}
            onChange={(e) => setES({ tintEnabled: e.target.checked })}
            style={{ accentColor: "#64748b" }}
          />
          <span style={{ fontSize: 12 }}>Tint over lifetime (start → end)</span>
        </label>

        <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 8 }}>
          <div>
            <div style={labelStyle}>Start color</div>
            <input
              type="text"
              value={tintStart}
              onChange={(e) => setES({ tintStart: e.target.value })}
              placeholder="#ffffff"
              style={{
                width: "100%",
                background: "#0b0d12",
                color: "white",
                border: "1px solid #374151",
                borderRadius: 8,
                padding: "6px 8px",
                fontSize: 12,
                boxSizing: "border-box",
              }}
            />
          </div>
          <div>
            <div style={labelStyle}>End color</div>
            <input
              type="text"
              value={tintEnd}
              onChange={(e) => setES({ tintEnd: e.target.value })}
              placeholder="#ffffff"
              style={{
                width: "100%",
                background: "#0b0d12",
                color: "white",
                border: "1px solid #374151",
                borderRadius: 8,
                padding: "6px 8px",
                fontSize: 12,
                boxSizing: "border-box",
              }}
            />
          </div>
        </div>

      </div>
    </CollapsibleBox>
  );
}

export { GlowPanel };
